import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { isPaidStatus } from '../utils/billing';

function normalizePlan(plan) {
  if (!plan) return 'starter';
  if (plan === 'free') return 'starter';
  if (
    plan === 'starter' ||
    plan === 'listing_only_private' ||
    plan === 'listing_only_agency' ||
    plan === 'pro' ||
    plan === 'agency'
  ) {
    return plan;
  }
  return 'starter';
}

export default function useBilling() {
  const { user } = useAuth();
  const functions = getFunctions();
  const [billingDoc, setBillingDoc] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [billingReady, setBillingReady] = useState(false);
  const [userReady, setUserReady] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setBillingDoc(null);
      setUserDoc(null);
      setBillingReady(true);
      setUserReady(true);
      return undefined;
    }

    setBillingReady(false);
    setUserReady(false);

    const billingRef = doc(db, 'users', user.uid, 'billing', 'subscription');
    const userRef = doc(db, 'users', user.uid);

    const unsubscribeBilling = onSnapshot(
      billingRef,
      (snap) => {
        setBillingDoc(snap.exists() ? snap.data() : null);
        setBillingReady(true);
      },
      () => {
        setBillingReady(true);
      }
    );

    const unsubscribeUser = onSnapshot(userRef, (snap) => {
      setUserDoc(snap.exists() ? snap.data() : null);
      setUserReady(true);
    });

    return () => {
      unsubscribeBilling();
      unsubscribeUser();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    if (!(billingReady && userReady)) return;

    const status = billingDoc?.status ?? 'none';
    const currentPeriodEnd = billingDoc?.currentPeriodEnd ?? null;
    const paid = isPaidStatus(status, currentPeriodEnd);
    if (paid) return;

    const pendingPlan = normalizePlan(
      sessionStorage.getItem(`checkoutPlan:${user.uid}`) ??
      null
    );
    if (!pendingPlan || pendingPlan === 'starter') return;

    // Retry periodically while plan is pending, so status recovers even if webhook was delayed.
    const throttleKey = `billingSyncLastAttemptAt:${user.uid}`;
    const lastAttempt = Number(sessionStorage.getItem(throttleKey) || 0);
    const now = Date.now();
    if (lastAttempt > 0 && now - lastAttempt < 15000) return;
    sessionStorage.setItem(throttleKey, String(now));

    const sync = httpsCallable(functions, 'syncSubscriptionStatus');
    sync({}).catch(() => {
      // Silent fallback: next throttled retry will try again.
    });
  }, [user?.uid, billingReady, userReady, billingDoc, userDoc, functions]);

  useEffect(() => {
    if (!user?.uid) return;
    const status = billingDoc?.status ?? 'none';
    const currentPeriodEnd = billingDoc?.currentPeriodEnd ?? null;
    if (!isPaidStatus(status, currentPeriodEnd)) return;
    sessionStorage.removeItem(`checkoutPlan:${user.uid}`);
    sessionStorage.removeItem(`billingCheckoutSuccessAt:${user.uid}`);
  }, [user?.uid, billingDoc]);

  return useMemo(() => {
    const loading = !(billingReady && userReady);
    const status = billingDoc?.status ?? 'none';
    const plan = normalizePlan(billingDoc?.plan ?? 'starter');
    const currentPeriodEnd = billingDoc?.currentPeriodEnd ?? null;
    const isPaid = isPaidStatus(status, currentPeriodEnd);
    const effectivePlan = isPaid ? plan : 'starter';
    const selectedStarter = userDoc?.selectedPlan === 'starter' ? 'starter' : null;
    const checkoutPlan = user?.uid ? sessionStorage.getItem(`checkoutPlan:${user.uid}`) : null;
    const checkoutSuccessAt = Number(
      user?.uid ? sessionStorage.getItem(`billingCheckoutSuccessAt:${user.uid}`) || 0 : 0
    );
    const hasRecentCheckoutSuccess = checkoutSuccessAt > 0 && (Date.now() - checkoutSuccessAt) < 3 * 60 * 1000;
    const postPaymentFallbackPlan =
      hasRecentCheckoutSuccess && checkoutPlan && checkoutPlan !== 'starter'
        ? checkoutPlan
        : null;
    const rawSelectedPlan = isPaid ? plan : (selectedStarter || postPaymentFallbackPlan);
    const selectedPlan = rawSelectedPlan ? normalizePlan(rawSelectedPlan) : null;
    const hasSelectedPlan = Boolean((isPaid && plan) || selectedStarter || postPaymentFallbackPlan);

    return {
      plan: effectivePlan,
      selectedPlan: hasSelectedPlan ? selectedPlan : null,
      hasSelectedPlan,
      status,
      isPaid,
      isPro: effectivePlan === 'pro',
      isAgency: effectivePlan === 'agency',
      loading,
      allowLaunch: userDoc?.allowLaunch !== false,
      isLaunchDiscount: !!userDoc?.isLaunchDiscount,
      stripeCustomerId: billingDoc?.customerId ?? billingDoc?.stripeCustomerId ?? userDoc?.stripeCustomerId ?? null,
      stripeSubscriptionId: billingDoc?.subscriptionId ?? billingDoc?.stripeSubscriptionId ?? userDoc?.subscriptionId ?? null,
      priceId: billingDoc?.priceId ?? null,
      cancelAtPeriodEnd: billingDoc?.cancelAtPeriodEnd ?? false,
      currentPeriodEnd
    };
  }, [billingDoc, userDoc, billingReady, userReady, user?.uid]);
}
